/**
 * Product detail (spec section 18).
 *
 * Everything the spec lists, plus the provenance block: last updated, data
 * source, and data quality — with the specific missing fields named.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ApiError, getHistory, getProduct, type ApiMeta, type HistoryPayload } from '@/api/client';
import { METRICS, computeMetrics, primaryPriceMetric, type MetricId } from '@/lib/nutrition/calculations';
import { labelForBasis } from '@/lib/nutrition/normalization';
import { scoreAll } from '@/lib/nutrition/scoring';
import type { Product } from '@/types/product';
import {
  formatCurrency,
  formatDate,
  formatMetric,
  formatPackage,
  formatSignedPercent,
  metricTitle,
  providerLabel,
} from '@/lib/format';
import { trimNumber } from '@/lib/nutrition/units';
import { useAppState } from '@/state/AppState';
import { MacroBar } from '@/components/MacroBar';
import { DataQualityPanel } from '@/components/DataQualityBadge';
import { Notice, ProvenanceLine, TargetContribution, ValueScorePanel } from '@/components/Panels';
import { StorePricingNotice } from '@/components/StoreCoverage';
import { RankPanel } from '@/components/RankPanel';
import { LoadingBar } from '@/components/LoadingBar';
import { AdSlot } from '@/components/AdSlot';
import { SourceBadge, sourceDetail } from '@/components/SourceBadge';
import { PriceHistoryChart } from '@/components/charts/PriceHistoryChart';

const DETAIL_METRICS: MetricId[] = [
  'proteinPerDollar',
  'kcalPerDollar',
  'pricePer100kcal',
  'pricePer10gProtein',
  'proteinPer100kcal',
  'proteinPctCalories',
];

export function ProductPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const location = useLocation();
  const { targets, toggleCompare, isComparing, compareFull, criteria } = useAppState();

  /*
   * A product handed over by the ranking or search that linked here.
   *
   * It lets the page render at once, and — the reason it exists — keeps the page
   * working when the API is briefly over its read limit: the rankings the reader
   * came from are static and never fail, so the product they just clicked is
   * already in hand. A direct visit has no seed and falls back to the API's
   * honest "try again" message.
   */
  const seeded = (location.state as { product?: Product } | null)?.product ?? null;

  const [product, setProduct] = useState<Product | null>(seeded);
  const [meta, setMeta] = useState<ApiMeta | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [history, setHistory] = useState<HistoryPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(seeded === null);

  useEffect(() => {
    const controller = new AbortController();
    // With a seed we already have something to show; refresh quietly behind it.
    setProduct(seeded);
    setLoading(seeded === null);
    setError(null);

    getProduct(id, controller.signal)
      .then((result) => {
        setProduct(result.data);
        setMeta(result.meta);
        setFromCache(result.fromCache);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // A seeded product stays on screen rather than being replaced by an
        // error — the reader can still read everything the ranking carried.
        if (seeded !== null) return;
        setError(err instanceof ApiError ? err.message : 'Could not load this product.');
      })
      .finally(() => setLoading(false));

    getHistory(id, 30)
      .then((result) => setHistory(result.data))
      .catch(() => setHistory(null));

    return () => controller.abort();
  }, [id]);

  const metrics = useMemo(() => (product === null ? null : computeMetrics(product)), [product]);
  const score = useMemo(() => {
    if (metrics === null) return null;
    // A single product has nothing to be scored against, so the score panel is
    // shown only for its component breakdown, not as a comparative number.
    return scoreAll([metrics]).get(metrics.productId) ?? null;
  }, [metrics]);

  if (loading) {
    return (
      <>
        <LoadingBar active />
        <div className="spinner-row">Loading product…</div>
      </>
    );
  }

  if (error !== null || product === null || metrics === null) {
    return (
      <>
        <Notice kind="error" title="Could not load this product.">
          {error ?? 'Not found.'}
        </Notice>
        <Link className="btn" to="/">
          ← Back to search
        </Link>
      </>
    );
  }

  const currency = metrics.currency;
  const per100 = metrics.per100;
  const basis = per100 === null ? null : labelForBasis(per100.basis);
  const normalizedPrice = primaryPriceMetric(metrics.packageKind);

  const nutritionRows: Array<{ label: string; value: number | null; unit: string }> = [
    { label: 'Energy', value: per100?.nutrients.kcal ?? null, unit: 'kcal' },
    { label: 'Protein', value: per100?.nutrients.protein ?? null, unit: 'g' },
    { label: 'Carbohydrate', value: per100?.nutrients.carbohydrate ?? null, unit: 'g' },
    { label: '  of which sugars', value: per100?.nutrients.sugar ?? null, unit: 'g' },
    { label: 'Fat', value: per100?.nutrients.fat ?? null, unit: 'g' },
    { label: '  of which saturates', value: per100?.nutrients.saturatedFat ?? null, unit: 'g' },
    { label: 'Fibre', value: per100?.nutrients.fibre ?? null, unit: 'g' },
    { label: 'Sodium', value: per100?.nutrients.sodium ?? null, unit: 'mg' },
  ];

  const sources = [
    // 'Price' says UNCHANGED SINCE, not 'checked at': the upsert now skips a
    // row whose price has not moved, so this timestamp is when the amount last
    // changed. Calling it a retrieval time would overstate freshness.
    product.price && { label: 'Price unchanged since', provider: providerLabel(product.price.source.provider), retrievedAt: product.price.source.retrievedAt },
    per100 && { label: 'Nutrition', provider: providerLabel(per100.source.provider), retrievedAt: per100.source.retrievedAt },
    product.quantity && { label: 'Package size', provider: providerLabel(product.quantity.source.provider), retrievedAt: product.quantity.source.retrievedAt },
  ].filter((s): s is { label: string; provider: string; retrievedAt: string } => Boolean(s));


  return (
    <>
      <Link className="btn btn-sm" to="/" style={{ marginBottom: '0.75rem' }}>
        ← Back to search
      </Link>

      <div className="detail-grid">
        <div>
          <section className="panel">
            <div className="detail-head">
              {product.imageUrl === null ? (
                <div className="detail-image-placeholder" aria-hidden="true">
                  ▤
                </div>
              ) : (
                <img className="detail-image" src={product.imageUrl} alt="" loading="lazy" />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1>{product.name}</h1>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.2rem 0 0' }}>
                  {product.brand ?? 'Unbranded'}
                  {product.category && ` · ${product.category}`}
                </p>
                <p style={{ margin: '0.4rem 0 0' }}>
                  <span style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {formatCurrency(metrics.values.price.value, currency)}
                  </span>{' '}
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {formatPackage(product.quantity)}
                  </span>
                </p>
              </div>
            </div>

            <dl className="kv-list">
              <div className="kv-row">
                <dt>Store</dt>
                <dd>{product.store?.name ?? 'N/A'}</dd>
              </div>
              <div className="kv-row">
                <dt>Location</dt>
                <dd>{product.store?.address ?? product.store?.location ?? 'N/A'}</dd>
              </div>
              <div className="kv-row">
                <dt>Availability</dt>
                <dd>{product.store?.online === false ? 'Physical store' : 'Online'}</dd>
              </div>
              <div className="kv-row">
                <dt>{METRICS[normalizedPrice].label}</dt>
                <dd title={metricTitle(normalizedPrice, metrics.values[normalizedPrice])}>
                  {formatMetric(normalizedPrice, metrics.values[normalizedPrice], currency)}
                </dd>
              </div>
            </dl>

            <StorePricingNotice />

            {product.productUrl && (
              <p style={{ margin: '0.75rem 0 0' }}>
                <a href={product.productUrl} target="_blank" rel="noopener noreferrer">
                  View at {product.store?.name ?? 'retailer'} ↗
                </a>
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                className={`btn${isComparing(product.id) ? ' btn-primary' : ''}`}
                onClick={() => toggleCompare(product.id, product.name)}
                disabled={!isComparing(product.id) && compareFull}
                aria-pressed={isComparing(product.id)}
              >
                {isComparing(product.id) ? '✓ In comparison' : 'Add to comparison'}
              </button>
            </div>
          </section>

          <section className="panel">
            <h2 className="panel-title">
              Nutrition {basis === null ? '' : `(${basis})`}
              {per100 !== null && (
                <SourceBadge kind={per100.source.kind ?? 'barcode_exact'} />
              )}
            </h2>
            {per100 === null ? (
              <Notice kind="warn" title="Insufficient data.">
                {metrics.per100Reason ?? 'No nutrition information is available for this product.'}
              </Notice>
            ) : (
              <>
                <table className="nutrition-table">
                  <tbody>
                    {nutritionRows.map((row) => (
                      <tr key={row.label}>
                        <td style={{ whiteSpace: 'pre' }}>{row.label}</td>
                        <td className={row.value === null ? 'na' : ''}>
                          {row.value === null ? 'N/A' : `${trimNumber(row.value, 1)} ${row.unit}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: '0.75rem' }}>
                  <MacroBar macros={metrics.macros} />
                </div>

                {/*
                  Which record these figures came from, and when.

                  A barcode should identify one exact pack, but nutrition
                  databases are contributed by the public and a code is
                  occasionally attached to the wrong variant. Naming the matched
                  record lets a reader spot a wrong match — "this says 500 ml and
                  I am holding the 1.5 L" — which no automatic check can
                  guarantee to catch.
                */}
                {/*
                  What these figures describe, and where they came from.

                  For a generic value this is the whole point: the reader must
                  see that it is a reference for the food, not the pack.
                */}
                {per100.source.kind === 'generic_reference' ? (
                  <p className="generic-caveat">
                    <span aria-hidden="true">≈</span>
                    <span>
                      <strong>Generic estimate.</strong> {sourceDetail('generic_reference')}
                      {per100.source.note != null && <> Based on {per100.source.note}.</>}
                    </span>
                  </p>
                ) : (
                  <p className="match-note">
                    {per100.source.note != null && per100.source.note !== ''
                      ? per100.source.note
                      : 'Matched by barcode'}
                    {per100.source.externalId != null && ` · ${per100.source.externalId}`}
                    {' · read '}
                    {new Date(per100.source.retrievedAt).toLocaleString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                )}

              </>
            )}
          </section>

          <section className="panel">
            <h2 className="panel-title">Calculated value metrics</h2>
            <dl className="kv-list">
              {DETAIL_METRICS.map((metric) => {
                const value = metrics.values[metric];
                return (
                  <div className="kv-row" key={metric}>
                    <dt title={METRICS[metric].formula}>{METRICS[metric].label}</dt>
                    <dd className={value.value === null ? 'na' : ''} title={metricTitle(metric, value)}>
                      {formatMetric(metric, value, currency, metrics.packageKind)}
                    </dd>
                  </div>
                );
              })}
            </dl>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.6rem 0 0' }}>
              Per-dollar metrics use whole-package totals. Density metrics use the{' '}
              {basis ?? 'per-100'} basis. Anything marked N/A could not be calculated from the
              available data — it is not zero.
            </p>
          </section>
        </div>

        <div>
          {score && (
            <ValueScorePanel score={score} metrics={metrics} topMetricLabel={null} />
          )}

          <RankPanel productId={product.id} selectedBoardIds={criteria} />

          <AdSlot slot="product-below-ranks" />

          <TargetContribution metrics={metrics} targets={targets} />

          <section className="panel">
            <h2 className="panel-title">Price history</h2>
            {history?.history ? (
              <>
                <dl className="kv-list" style={{ marginBottom: '0.6rem' }}>
                  <div className="kv-row">
                    <dt>Current</dt>
                    <dd>{formatCurrency(metrics.values.price.value, currency)}</dd>
                  </div>
                  <div className="kv-row">
                    <dt>Lowest recorded</dt>
                    <dd>{formatCurrency(history.history.min, history.history.currency)}</dd>
                  </div>
                  <div className="kv-row">
                    <dt>Highest recorded</dt>
                    <dd>{formatCurrency(history.history.max, history.history.currency)}</dd>
                  </div>
                  <div className="kv-row">
                    <dt>Average</dt>
                    <dd>{formatCurrency(history.history.avg, history.history.currency)}</dd>
                  </div>
                  <div className="kv-row">
                    <dt>Change over period</dt>
                    <dd>{formatSignedPercent(history.history.changePct)}</dd>
                  </div>
                  <div className="kv-row">
                    <dt>Period covered</dt>
                    <dd style={{ fontSize: '0.78rem' }}>
                      {formatDate(history.history.windowStart)} – {formatDate(history.history.windowEnd)}
                    </dd>
                  </div>
                </dl>
                <PriceHistoryChart history={history.history} note={history.note} />
              </>
            ) : (
              <p className="chart-empty">
                {history?.note ?? 'No price history has been recorded for this product yet.'}
              </p>
            )}
          </section>


          <DataQualityPanel
            quality={product.quality}
            sources={sources}
            lastUpdated={product.lastUpdated}
          />

          {meta && (
            <div style={{ marginTop: '0.75rem' }}>
              <ProvenanceLine
                source={meta.source}
                generatedAt={meta.generatedAt}
                cached={meta.cached}
                fromCache={fromCache}
                isSampleData={meta.isSampleData}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
